import React, { useState, useEffect } from "react";
import FaiTypography from "MuiComponents/FaiTypography";
import FaiBox from "MuiComponents/FaiBox";
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Grid,
	MenuItem,
	TextField,
} from "@mui/material";
import { message } from "antd";
import Axios from "services/api/config";
import {
	getAllOrdersURL,
	getAllReturnsURL,
	getAllRefundsURL,
} from "services/api/routes/Order";
import { getPaymentsList } from "services/api/routes/Payments";

// Trigger a client-side CSV download.
const downloadCSV = (rows: (string | number)[][], filename: string) => {
	const csv = rows
		.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
		.join("\n");
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
};

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : "-");
const names = (items: any[]) =>
	(items || []).map((it) => it?.product?.productName).filter(Boolean).join("; ") || "-";

type Column = { header: string; value: (row: any) => string | number };
type ReportDef = {
	key: string;
	label: string;
	// Backend caps every list endpoint at size<=100, so reports are assembled by
	// looping pages of 100 (see fetchAllPages) — never a single oversized request.
	page: (page: number, size: number) => { url: string; method: string };
	dateField?: string; // row field used by the date-range filter (if any)
	filename: string;
	columns: Column[];
};

// Each report maps a paginated admin endpoint to a flat set of CSV columns.
const REPORTS: ReportDef[] = [
	{
		key: "orders",
		label: "Orders",
		page: (p, s) => getAllOrdersURL({ page: p, size: s }),
		dateField: "createdAt",
		filename: "platform-orders-report.csv",
		columns: [
			{ header: "Order ID", value: (o) => o?.razorpayOrderId || o?.id || "-" },
			{ header: "Customer", value: (o) => o?.address?.fullName || "-" },
			{ header: "Products", value: (o) => names(o?.items) },
			{ header: "Payment Mode", value: (o) => o?.paymentMode || "-" },
			{ header: "Amount", value: (o) => o?.totalPrice ?? 0 },
			{ header: "Amount Paid", value: (o) => o?.amountPaid ?? o?.totalPrice ?? 0 },
			{ header: "Coupon", value: (o) => o?.couponCode || "-" },
			{ header: "Coupon Discount", value: (o) => o?.couponDiscount ?? 0 },
			{ header: "Status", value: (o) => o?.status || "-" },
			{ header: "Date", value: (o) => fmtDate(o?.createdAt) },
		],
	},
	{
		key: "payments",
		label: "Payments / Payouts",
		page: (p, s) => getPaymentsList(`page=${p}&size=${s}`),
		dateField: "createdAt",
		filename: "platform-payouts-report.csv",
		columns: [
			{ header: "Payout ID", value: (r) => r?.id || "-" },
			{ header: "Merchant ID", value: (r) => r?.merchantId || "-" },
			{ header: "Sub-order", value: (r) => r?.subOrderId || "-" },
			{ header: "Gross", value: (r) => r?.grossAmount ?? 0 },
			{ header: "Commission %", value: (r) => r?.platformCommissionPercentage ?? 0 },
			{ header: "Commission", value: (r) => r?.commissionAmount ?? 0 },
			{ header: "Net Payout", value: (r) => r?.netAmount ?? 0 },
			{ header: "Status", value: (r) => r?.status || "-" },
			{ header: "Date", value: (r) => fmtDate(r?.createdAt) },
		],
	},
	{
		key: "customers",
		label: "Customers",
		page: (p, s) => ({ url: `/admin/customers?page=${p}&size=${s}`, method: "GET" }),
		filename: "platform-customers-report.csv",
		columns: [
			{ header: "Customer ID", value: (c) => c?.id || "-" },
			{ header: "Name", value: (c) => c?.name || "-" },
			{ header: "Email", value: (c) => c?.email || "-" },
			{ header: "Mobile", value: (c) => c?.mobile || "-" },
			{ header: "Orders", value: (c) => c?.numOfOrders ?? 0 },
			{ header: "Total Spent", value: (c) => c?.totalAmount ?? 0 },
		],
	},
	{
		key: "merchants",
		label: "Merchants",
		page: (p, s) => ({ url: `/admin/merchants/approved?page=${p}&size=${s}`, method: "GET" }),
		dateField: "createdAt",
		filename: "platform-merchants-report.csv",
		columns: [
			{ header: "Merchant ID", value: (m) => m?.id || "-" },
			{ header: "Company", value: (m) => m?.businessInformation?.companyName || "-" },
			{
				header: "Contact",
				value: (m) =>
					[m?.contactInformation?.firstName, m?.contactInformation?.lastName]
						.filter(Boolean)
						.join(" ") || "-",
			},
			{ header: "Email", value: (m) => m?.email || "-" },
			{ header: "Phone", value: (m) => m?.contactInformation?.primaryContactNo || "-" },
			{ header: "Status", value: (m) => m?.accountStatus || "-" },
			{ header: "Joined", value: (m) => fmtDate(m?.createdAt) },
		],
	},
	{
		key: "returns",
		label: "Returns",
		page: (p, s) => getAllReturnsURL({ page: p, size: s }),
		dateField: "orderDate",
		filename: "platform-returns-report.csv",
		columns: [
			{ header: "Return ID", value: (r) => r?.id || "-" },
			{ header: "Product", value: (r) => r?.productName || "-" },
			{ header: "Qty", value: (r) => r?.quantity ?? 0 },
			{ header: "Amount", value: (r) => r?.amount ?? 0 },
			{ header: "Reason", value: (r) => r?.returnReason || "-" },
			{ header: "Status", value: (r) => r?.status || "-" },
			{ header: "Order Date", value: (r) => fmtDate(r?.orderDate) },
		],
	},
	{
		key: "refunds",
		label: "Refunds",
		page: (p, s) => getAllRefundsURL({ page: p, size: s }),
		dateField: "createdAt",
		filename: "platform-refunds-report.csv",
		columns: [
			{ header: "Refund ID", value: (r) => r?.id || "-" },
			{ header: "Product", value: (r) => r?.productName || "-" },
			{ header: "Qty", value: (r) => r?.quantity ?? 0 },
			{ header: "Amount", value: (r) => r?.amount ?? 0 },
			{ header: "Type", value: (r) => r?.refundType || "-" },
			{ header: "Destination", value: (r) => r?.refundDestination || "-" },
			{ header: "Reason", value: (r) => r?.reason || "-" },
			{ header: "Status", value: (r) => r?.status || "-" },
			{ header: "Date", value: (r) => fmtDate(r?.createdAt) },
		],
	},
];

function StatCard({ bgColor, title, value }: any) {
	return (
		<FaiBox p={3} sx={{ borderRadius: 2, backgroundColor: bgColor, color: "#fff", minHeight: 110 }}>
			<FaiTypography variant={"h6"} color={"inherit"}>{title}</FaiTypography>
			<FaiTypography variant={"h3"} fontWeight={"bold"} color={"inherit"}>{value}</FaiTypography>
		</FaiBox>
	);
}

const Reports: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [downloading, setDownloading] = useState(false);
	const [totals, setTotals] = useState({ orders: 0, returns: 0, refunds: 0, payouts: 0 });
	const [reportKey, setReportKey] = useState("orders");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	const selected = REPORTS.find((r) => r.key === reportKey) as ReportDef;

	// totalElements is exact and cheap to read with size=1.
	const countOf = async (route: any) => {
		try {
			const { data } = await Axios({ ...route });
			return data?.data?.totalElements || 0;
		} catch {
			return 0;
		}
	};

	useEffect(() => {
		const run = async () => {
			setLoading(true);
			const [orders, returns, refunds, payouts] = await Promise.all([
				countOf(getAllOrdersURL({ page: 0, size: 1 })),
				countOf(getAllReturnsURL({ page: 0, size: 1 })),
				countOf(getAllRefundsURL({ page: 0, size: 1 })),
				countOf(getPaymentsList("page=0&size=1")),
			]);
			setTotals({ orders, returns, refunds, payouts });
			setLoading(false);
		};
		run();
	}, []);

	// Pull every page (size=100, the backend max) until the last one. Capped at
	// 100 pages (10k rows) so a runaway never loops forever.
	const fetchAllPages = async (def: ReportDef) => {
		const all: any[] = [];
		for (let page = 0; page < 100; page++) {
			const { data } = await Axios({ ...def.page(page, 100) });
			const d = data?.data || {};
			const batch: any[] = d.data || [];
			all.push(...batch);
			if (d.last === true || batch.length < 100) break;
		}
		return all;
	};

	const exportReport = async () => {
		try {
			setDownloading(true);
			let rows = await fetchAllPages(selected);

			// Apply the date range only to reports that carry a date field.
			const from = startDate ? new Date(startDate).getTime() : null;
			const to = endDate ? new Date(endDate).getTime() + 86400000 : null; // inclusive end day
			if (selected.dateField && (from || to)) {
				const field = selected.dateField;
				rows = rows.filter((r: any) => {
					const t = r?.[field] ? new Date(r[field]).getTime() : 0;
					if (from && t < from) return false;
					if (to && t > to) return false;
					return true;
				});
			}

			const range = selected.dateField && (startDate || endDate)
				? ` (${startDate || "…"} to ${endDate || "…"})`
				: "";
			const csv: (string | number)[][] = [
				[`${selected.label} report${range}`],
				[`Generated ${new Date().toLocaleString()}`],
				[],
				selected.columns.map((c) => c.header),
				...rows.map((r) => selected.columns.map((c) => c.value(r))),
			];
			downloadCSV(csv, selected.filename);
			message.success(`Exported ${rows.length} ${selected.label.toLowerCase()} record(s)`);
		} catch (e) {
			console.error("Error exporting report:", e);
			message.error("Failed to export report");
		} finally {
			setDownloading(false);
		}
	};

	return (
		<Card className="my-6 p-3 md:p-6">
			<FaiTypography variant="h4" className="!text-xl !font-semibold" mb={1}>
				Reports
			</FaiTypography>
			<p className="text-sm mb-4">
				Platform-wide totals across all merchants and customers. Choose a report
				type and export it as CSV, optionally filtered by a date range.
			</p>

			{loading ? (
				<Box className="flex justify-center p-6">
					<CircularProgress />
				</Box>
			) : (
				<>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6} md={3}>
							<StatCard bgColor={"#10459E"} title={"Total Orders"} value={totals.orders} />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<StatCard bgColor={"#1AB595"} title={"Payouts"} value={totals.payouts} />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<StatCard bgColor={"#E99956"} title={"Returns"} value={totals.returns} />
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<StatCard bgColor={"#FF6A6A"} title={"Refunds"} value={totals.refunds} />
						</Grid>
					</Grid>

					<Box sx={{ bgcolor: "#F5F5F5", borderRadius: 2, p: 2, mt: 4 }} className="flex flex-col md:flex-row md:items-end gap-3">
						<Box sx={{ minWidth: 200 }}>
							<FaiTypography variant="caption" className="!font-semibold">Report type</FaiTypography>
							<TextField
								select
								size="small"
								fullWidth
								value={reportKey}
								onChange={(e) => setReportKey(e.target.value)}
							>
								{REPORTS.map((r) => (
									<MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
								))}
							</TextField>
						</Box>
						<Box>
							<FaiTypography variant="caption" className="!font-semibold">Start date</FaiTypography>
							<TextField
								type="date"
								size="small"
								fullWidth
								value={startDate}
								onChange={(e) => setStartDate(e.target.value)}
								disabled={!selected.dateField}
								InputLabelProps={{ shrink: true }}
							/>
						</Box>
						<Box>
							<FaiTypography variant="caption" className="!font-semibold">End date</FaiTypography>
							<TextField
								type="date"
								size="small"
								fullWidth
								value={endDate}
								onChange={(e) => setEndDate(e.target.value)}
								disabled={!selected.dateField}
								InputLabelProps={{ shrink: true }}
							/>
						</Box>
						<Button variant="contained" color="primary" disabled={downloading} onClick={exportReport}>
							{downloading ? "Exporting…" : `Download ${selected.label} CSV`}
						</Button>
					</Box>
					{!selected.dateField && (
						<FaiTypography variant="caption" className="!block !mt-2 !text-gray-500">
							The date filter doesn't apply to the {selected.label} report.
						</FaiTypography>
					)}
				</>
			)}
		</Card>
	);
};

export default Reports;
